'use client';

import React from 'react';
import { useSWRConfig } from 'swr';
import { ToastProvider } from '@/components/ui/Toast';
import { SWRProvider } from '@/lib/swr';
import { SocketProvider } from '@/lib/websocket';
import { getStoredBuildId, purgeSiteCache, setStoredBuildId } from '@/utils/siteCache';
import VfxProvider from '@/components/VfxProvider';
import AuthRefreshPoller from '@/components/AuthRefreshPoller';
import SiteThemeProvider from '@/components/SiteThemeProvider';
import SiteBackground from '@/components/SiteBackground';

// Обработчик push-уведомлений для инвалидации SWR кэша
function PushInvalidationHandler() {
  const { mutate } = useSWRConfig();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    const onMsg = (e: MessageEvent) => {
      const d: any = (e as any)?.data;
      if (!d || d.type !== 'lisad_push') return;
      const payload: any = d.data || {};
      const cmd: any = payload?.cmd;
      if (!cmd || cmd.type !== 'invalidate') return;
      const targets: string[] = Array.isArray(cmd.targets) ? cmd.targets.map((x: any) => String(x || '').trim()) : [];
      if (!targets.length) return;

      // Инвалидируем соответствующие ключи в SWR кэше
      if (targets.includes('news') || targets.includes('all')) {
        void mutate((key) => typeof key === 'string' && key.includes('/api/news'));
      }
      if (targets.includes('projects') || targets.includes('all')) {
        void mutate((key) => typeof key === 'string' && key.includes('/api/projects'));
      }
      if (targets.includes('vacancies') || targets.includes('all')) {
        void mutate((key) => typeof key === 'string' && key.includes('/api/vacancies'));
      }
      if (targets.includes('settings') || targets.includes('all')) {
        void mutate((key) => typeof key === 'string' && key.includes('/api/settings'));
      }
    };

    try {
      navigator.serviceWorker.addEventListener('message', onMsg as any);
    } catch {
      return;
    }
    return () => {
      try {
        navigator.serviceWorker.removeEventListener('message', onMsg as any);
      } catch {}
    };
  }, [mutate]);

  return null;
}

// Обработчик очистки кэша при деплое
function DeployCacheHandler() {
  React.useEffect(() => {
    const cur = String(process.env.NEXT_PUBLIC_APP_BUILD_ID || '').trim();
    if (!cur) return;
    const prev = getStoredBuildId();
    if (prev && prev !== cur) {
      setStoredBuildId(cur);
      // 500ms задержка для завершения очистки кэша
      void purgeSiteCache().finally(() => setTimeout(() => window.location.reload(), 500));
      return;
    }
    if (!prev) setStoredBuildId(cur);
  }, []);

  return null;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SWRProvider>
      <SiteThemeProvider>
      <SocketProvider>
        <ToastProvider>
          <VfxProvider>
            <SiteBackground />
            <DeployCacheHandler />
            <PushInvalidationHandler />
            <AuthRefreshPoller />
            {children}
          </VfxProvider>
        </ToastProvider>
      </SocketProvider>
      </SiteThemeProvider>
    </SWRProvider>
  );
}

